import { AxiosInstance, AxiosRequestConfig } from "axios"
import SessaoServico from "@/servicos/sessao.service"
import { renovarToken } from "@/servicos/refresh.service"
import { encerrarSessaoAutenticada } from "@/servicos/logout.service"

interface RequisicaoComTentativaDeRenovacao extends AxiosRequestConfig {
  _tentouRenovarToken?: boolean
}

interface ErroDeRequisicao {
  config?: RequisicaoComTentativaDeRenovacao
}

let renovacaoEmAndamento: Promise<void> | null = null

const ROTAS_SEM_RENOVACAO = [
  "/api/auth",
  "/api/ambientes-do-usuario",
  "/api/auth-user-pool",
  "/api/auth-cognito-forgot-password",
  "/api/esqueci-minha-senha",
  "/api/auth-confirm-migration-user-cognito",
  "/api/auth-logout",
  "/api/logout",
]

function ehRotaSemRenovacao(url: string | undefined): boolean {
  const caminho = url?.split("?")[0]
  return (
    caminho?.includes("/ambientes-do-usuario/") ||
    ROTAS_SEM_RENOVACAO.some((rota) => caminho?.endsWith(rota)) ||
    false
  )
}

function montarAuthorizationAtual(): string {
  return `${SessaoServico.tipo} ${SessaoServico.token}`
}

function coletarAuthorizationDaRequisicao(
  requisicao: RequisicaoComTentativaDeRenovacao
): string | undefined {
  const headers = requisicao.headers as
    | Record<string, string | undefined>
    | undefined
  return headers?.Authorization ?? headers?.authorization
}

function atualizarAuthorizationDaRequisicao(
  requisicao: RequisicaoComTentativaDeRenovacao
): void {
  requisicao.headers = {
    ...(requisicao.headers ?? {}),
    Authorization: montarAuthorizationAtual(),
  }
}

function encerrarSessaoSeAutenticada(): void {
  if (SessaoServico.autenticado) {
    encerrarSessaoAutenticada()
  }
}

async function renovarSessao(): Promise<void> {
  const refreshToken = SessaoServico.tokenRenovacao
  const accessToken = SessaoServico.token
  const tipoToken = SessaoServico.tipo

  if (!refreshToken || !accessToken || !tipoToken) {
    throw new Error("Sessão sem os tokens necessários para renovação")
  }

  const novosTokens = await renovarToken(refreshToken, tipoToken, accessToken)

  SessaoServico.token = novosTokens.access_token
  SessaoServico.tokenRenovacao = novosTokens.refresh_token
}

export async function verificarSeTokenEstaExpirado(
  error: ErroDeRequisicao,
  instancia: AxiosInstance
): Promise<unknown> {
  const requisicaoOriginal = error.config

  if (
    !SessaoServico.autenticado ||
    !requisicaoOriginal ||
    requisicaoOriginal._tentouRenovarToken ||
    ehRotaSemRenovacao(requisicaoOriginal.url)
  ) {
    return Promise.reject(error)
  }

  requisicaoOriginal._tentouRenovarToken = true

  const authorizationDaRequisicao =
    coletarAuthorizationDaRequisicao(requisicaoOriginal)
  if (
    authorizationDaRequisicao &&
    authorizationDaRequisicao !== montarAuthorizationAtual()
  ) {
    atualizarAuthorizationDaRequisicao(requisicaoOriginal)
    try {
      return await instancia.request(requisicaoOriginal)
    } catch {
      encerrarSessaoSeAutenticada()
      return Promise.reject(error)
    }
  }

  if (!SessaoServico.tokenRenovacao) {
    encerrarSessaoSeAutenticada()
    return Promise.reject(error)
  }

  try {
    if (!renovacaoEmAndamento) {
      renovacaoEmAndamento = renovarSessao().finally(() => {
        renovacaoEmAndamento = null
      })
    }

    await renovacaoEmAndamento

    atualizarAuthorizationDaRequisicao(requisicaoOriginal)

    return await instancia.request(requisicaoOriginal)
  } catch {
    encerrarSessaoSeAutenticada()
    return Promise.reject(error)
  }
}
